import apiClient from './meta-client';
import {
    MetaApp,
    CreateMetaAppInput,
    UpdateMetaAppInput,
    MetaUser,
    MetaPage,
    OAuthInitResponse,
    SyncPagesRequest,
    SyncPagesResponse,
    UpdatePageConfigInput,
    PageCategory,
    PageVariable,
    PageVariableInput,
    MetaAppUsageHistoryResponse,
    LinkedProfilesResponse,
    DebugTokenResponse,
    DebugTokenSimpleResponse,
    ConnectedUserPagesEntry,
    AlertContact,
    AlertContactInput,
} from '@/pages/Meta/Connection/types/meta.types';
import {
    BroadcastV2Settings,
    AppRateLimitListResponse,
    AppRateLimit,
} from '@/pages/Meta/Connection/types/broadcastV2.types';
import {
    OrchestratorSettingListResponse,
    OrchestratorSettingUpdateResponse,
    OrchestratorSettingHistoryResponse,
} from '@/pages/Meta/Connection/types/orchestratorSettings.types';

// ====================================
// META APPS
// ====================================

/**
 * Apps do Meta. `status` decide a lista.
 *
 * "Deletar app" é soft delete (`is_active = 0`): o app some da tela mas
 * continua ocupando o `app_id` no Meta. Sem a lista de inativos, quem apagou
 * e tentou recadastrar levava "App ID já está em uso" apontando para algo
 * invisível, sem nenhuma saída.
 */
export const getMetaApps = async (status: 'ativos' | 'inativos' = 'ativos'): Promise<MetaApp[]> => {
    const response = await apiClient.get('/meta/apps', { params: { status } });
    return response.data;
};

export const reactivateMetaApp = async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/meta/apps/${id}/reativar`);
    return response.data;
};

/** Corpo do 409 de `createMetaApp` quando o app_id já existe. */
export interface AppIdEmUsoDetalhe {
    erro: 'app_id_em_uso';
    mensagem: string;
    ativo: boolean;
    pode_reativar: boolean;
    app: {
        id: number;
        app_id: string;
        app_name: string;
        is_active: boolean;
        user_id: number | null;
    };
}

/** Reconhece o 409 estruturado sem confundir com erro de string. */
export const lerConflitoDeApp = (error: any): AppIdEmUsoDetalhe | null => {
    const d = error?.response?.data?.detail;
    return d && typeof d === 'object' && d.erro === 'app_id_em_uso' ? d : null;
};

export const getMetaApp = async (id: number): Promise<MetaApp> => {
    const response = await apiClient.get(`/meta/apps/${id}`);
    return response.data;
};

// Retorna o app com app_secret REAL (não mascarado). Só deve ser chamado ao abrir
// o modal de edição — nunca na listagem geral (que continua mascarada por segurança).
export const getMetaAppForEdit = async (id: number): Promise<MetaApp> => {
    const response = await apiClient.get(`/meta/apps/${id}/edit`);
    return response.data;
};

export const getDefaultMetaApp = async (): Promise<MetaApp> => {
    const response = await apiClient.get('/meta/apps/default');
    return response.data;
};

// Todos os escopos que a plataforma sempre precisa — não há mais escopos opcionais:
// o form não deixa a criação do app depender do usuário lembrar de marcar checkboxes.
const REQUIRED_SCOPES = [
    'pages_show_list',
    'pages_messaging',
    'pages_manage_metadata',
    'pages_utility_messaging',
    'business_management',
    'pages_read_engagement',
    'pages_read_user_content',
    'pages_manage_posts',
    'pages_manage_engagement',
];

function buildOauthScopes(scopes?: string[]): string[] {
    return Array.from(new Set([...REQUIRED_SCOPES, ...(scopes ?? [])]));
}

export const createMetaApp = async (data: CreateMetaAppInput): Promise<MetaApp> => {
    const { scopes, ...rest } = data;
    const response = await apiClient.post('/meta/apps', { ...rest, oauth_scopes: buildOauthScopes(scopes) });
    return response.data;
};

export const updateMetaApp = async (id: number, data: UpdateMetaAppInput): Promise<MetaApp> => {
    const { scopes, ...rest } = data;
    const response = await apiClient.put(`/meta/apps/${id}`, { ...rest, oauth_scopes: buildOauthScopes(scopes) });
    return response.data;
};

export const updateMetaAppStatus = async (id: number, is_active: boolean): Promise<{ message: string }> => {
    const response = await apiClient.patch(`/meta/apps/${id}/status`, { is_active });
    return response.data;
};

export const deleteMetaApp = async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/meta/apps/${id}`);
    return response.data;
};

/** O que some ao excluir. `definitivo`: app já inativo, o DELETE apaga de verdade. */
export interface AppImpactoExclusao {
    app_id: number;
    app_name: string;
    is_active: boolean;
    definitivo: boolean;
    perfis_ativos: number;
    perfis_inativos: number;
    paginas_ativas: number;
    paginas_inativas: number;
    paginas_sem_outro_app: number;
    tokens: number;
    /** Páginas fixadas neste app pelo Grupo de App (trava 1:1 página→app). */
    paginas_travadas: number;
}

export const getAppImpactoExclusao = async (id: number): Promise<AppImpactoExclusao> => {
    const response = await apiClient.get(`/meta/apps/${id}/exclusao`);
    return response.data;
};

// ====================================
// META APPS USAGE (rate limit observability)
// ====================================

export const getMetaAppUsageHistory = async (appId: string): Promise<MetaAppUsageHistoryResponse> => {
    const response = await apiClient.get(`/meta/apps-usage/${appId}/history`);
    return response.data;
};

// ====================================
// META OAUTH
// ====================================

export const initMetaOAuth = async (
    meta_app_id: number | null = null,
    scopes?: string[]
): Promise<OAuthInitResponse> => {
    const response = await apiClient.post('/meta/oauth/init', { meta_app_id, scopes });
    return response.data;
};

export const processOAuthCallback = async (code: string, state?: string): Promise<any> => {
    const params = new URLSearchParams({ code });
    if (state) params.append('state', state);
    const response = await apiClient.get(`/meta/oauth/callback?${params.toString()}`);
    return response.data;
};

// ====================================
// META USERS (Authentications)
// ====================================

/** Só admin: `userId` = perfis de um dono; `todos` = de todos. Sem nada = os do próprio usuário. */
export const getMetaUsers = async (userId?: number, todos?: boolean): Promise<MetaUser[]> => {
    const response = await apiClient.get('/meta/users', { params: { user_id: userId, todos: todos || undefined } });
    return response.data;
};

export const revokeMetaUser = async (authId: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/meta/users/${authId}`);
    return response.data;
};

export const debugMetaUserToken = async (authId: number): Promise<DebugTokenSimpleResponse> => {
    const response = await apiClient.post(`/meta/users/${authId}/debug-token-simple`);
    return response.data;
};

// ====================================
// META PAGES
// ====================================

export const getMetaPages = async (): Promise<MetaPage[]> => {
    const response = await apiClient.get('/meta/pages', { params: { all: true } });
    return Array.isArray(response.data) ? response.data : (response.data?.items ?? []);
};

// Páginas que perderam o último token nos últimos `dias`, inclusive as que o
// job de validação desativou — a lista normal só traz as ativas.
export const getMetaPagesComProblema = async (dias: number): Promise<MetaPage[]> => {
    const response = await apiClient.get('/meta/pages', { params: { problema_dias: dias } });
    return Array.isArray(response.data) ? response.data : [];
};

export interface PaginatedPages {
    items: MetaPage[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

export const getMetaPagesPaginated = async (
    page: number = 1,
    per_page: number = 20,
    search?: string,
): Promise<PaginatedPages> => {
    const params: Record<string, any> = { page, per_page };
    if (search) params.search = search;
    const response = await apiClient.get('/meta/pages', { params });
    return response.data;
};

export const getActiveMetaPages = async (): Promise<MetaPage[]> => {
    const response = await apiClient.get('/meta/pages/active');
    return response.data;
};

export interface PagesProblemsCountResponse {
    count: number;
}

export const getPagesProblemsCount = async (): Promise<PagesProblemsCountResponse> => {
    const response = await apiClient.get('/meta/pages/problems-count');
    return response.data;
};

export interface ProblemPageRef {
    page_id: string;
    page_name: string | null;
}

export interface PageProblemGroup {
    meta_user_id: number | null;
    meta_user_name: string;
    token_invalid_at: string | null;
    reason: string;
    pages: ProblemPageRef[];
    pages_count: number;
}

export interface PagesProblemsListResponse {
    groups: PageProblemGroup[];
    count: number;
}

export const getPagesProblemsList = async (): Promise<PagesProblemsListResponse> => {
    const response = await apiClient.get('/meta/pages/problems');
    return response.data;
};

// ─── Contingência de apps por página ────────────────────────────────────
// Erro 613 do Meta é limite POR APP (~12k req/hora medidas). Quando várias
// páginas grandes dividem o mesmo app, o 613 é matemático. Este endpoint
// cruza a fila real de cada página com o pool de apps vivos.

export interface ContingencyApp {
    app_id: string;
    app_name: string;
    meta_app_id: string | null;
    // load/usage_pct = carga de HOJE (leads ativos) — bate com o Grafana.
    // *_projected = o mesmo depois da regua de engajamento.
    load: number;
    load_projected: number;
    usage_pct: number;
    usage_pct_projected: number;
    pages: number;
    status: 'ok' | 'atencao' | 'critico';
    top_pages: { page_name: string; queue: number }[];
}

export interface ContingencyPage {
    page_id: string;
    page_name: string;
    queue: number;
    active_leads: number;
    apps: string[];
    apps_count: number;
    apps_missing: number;
    worst_app: string | null;
    worst_app_name: string | null;
    worst_app_usage: number;
    apps_detail: { id: string; name: string; meta_app_id: string | null; usage_pct: number }[];
    priority: 'critica' | 'atencao' | 'ok' | 'sem_app';
    // true = página tem 1 único app vivo — zero candidato de fallback.
    // O desvio automático do orquestrador exige um SEGUNDO token na própria
    // página; sem ele, saturado significa sem plano B, não só "apertado".
    single_app_lock: boolean;
}

export interface ContingencyResponse {
    kpis: {
        apps_total: number;
        apps_saturated: number;
        pages_total: number;
        pages_critical: number;
        pages_single_app: number;
        subscriptions_missing: number;
        apps_saturated_after_window: number;
        queue_total: number;
        queue_at_risk: number;
        projected_queue_total: number;
        projected_queue_at_risk: number;
        window_days: number;
    };
    apps: ContingencyApp[];
    pages: ContingencyPage[];
    config: { app_capacity_hour: number; window_days: number };
}

export const getPagesContingency = async (windowDays = 7): Promise<ContingencyResponse> => {
    const response = await apiClient.get('/meta/pages/contingency', {
        params: { window_days: windowDays },
    });
    return response.data;
};

export const syncMetaPages = async (data: SyncPagesRequest): Promise<SyncPagesResponse> => {
    const response = await apiClient.post('/meta/pages/sync', data);
    return response.data;
};

export const updateMetaPageStatus = async (id: number, is_active: boolean): Promise<{ message: string }> => {
    const response = await apiClient.patch(`/meta/pages/${id}/status`, { is_active });
    return response.data;
};

export const updateMetaPageConfig = async (id: number, data: UpdatePageConfigInput): Promise<{ message: string }> => {
    const response = await apiClient.patch(`/meta/pages/${id}/config`, data);
    return response.data;
};

export const bulkUpdateMetaPageConfig = async (data: {
    page_ids: number[];
    language?: string;
    timezone?: string;
    currency?: string;
    internal_category_id?: number | null;
}): Promise<{ updated: number; failed: { page_id: number; error: string }[] }> => {
    const response = await apiClient.patch('/meta/pages/bulk-config', data);
    return response.data;
};

export const deleteMetaPage = async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/meta/pages/${id}`);
    return response.data;
};

export const getPageLinkedProfiles = async (pageId: string): Promise<LinkedProfilesResponse> => {
    const response = await apiClient.get(`/meta/pages/${pageId}/linked-profiles`);
    return response.data;
};

export const debugPageToken = async (pageId: string, metaUserId?: number): Promise<DebugTokenResponse> => {
    const response = await apiClient.post(`/meta/pages/${pageId}/debug-token`, metaUserId ? { meta_user_id: metaUserId } : undefined);
    return response.data;
};

export const getUsersPagesMap = async (): Promise<ConnectedUserPagesEntry[]> => {
    const response = await apiClient.get('/meta/users-pages-map');
    return response.data;
};

// ====================================
// PAGE CATEGORIES
// ====================================

/** Só admin: `userId` = categorias de um dono; `todos` = de todos. Sem nada = as do próprio usuário. */
export const getPageCategories = async (userId?: number, todos?: boolean): Promise<PageCategory[]> => {
    const response = await apiClient.get('/meta/page-categories', { params: { user_id: userId, todos: todos || undefined } });
    return response.data;
};

// ====================================
// PAGE VARIABLES
// ====================================

export const getPageVariables = async (pageId: number): Promise<PageVariable[]> => {
    const response = await apiClient.get(`/meta/pages/${pageId}/variables`);
    return response.data;
};

export const getPageVariablesBatch = async (pageIds: number[]): Promise<Record<string, PageVariable[]>> => {
    const response = await apiClient.post('/meta/pages/variables-batch', pageIds);
    return response.data;
};

export const createPageVariable = async (pageId: number, data: PageVariableInput): Promise<PageVariable> => {
    const response = await apiClient.post(`/meta/pages/${pageId}/variables`, data);
    return response.data;
};

export const updatePageVariable = async (pageId: number, variableId: number, data: Partial<PageVariableInput>): Promise<PageVariable> => {
    const response = await apiClient.patch(`/meta/pages/${pageId}/variables/${variableId}`, data);
    return response.data;
};

export const deletePageVariable = async (pageId: number, variableId: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/meta/pages/${pageId}/variables/${variableId}`);
    return response.data;
};

export const bulkUpsertPageVariables = async (pageId: number, variables: PageVariableInput[]): Promise<PageVariable[]> => {
    const response = await apiClient.put(`/meta/pages/${pageId}/variables`, variables);
    return response.data;
};

// ====================================
// CAMPANHAS DUPLICADAS
// ====================================
//
// Campanha compartilhada entre páginas faz a receita ser DIVIDIDA entre elas:
// a origem devolve um valor por utm_campaign e não diz quem gerou o quê.

export interface CampanhaDuplicadaPagina {
    id: number;
    page_id: string;
    nome: string;
    idioma: string | null;
    ativa: boolean;
}

export interface CampanhaDuplicadaGrupo {
    chave: 'UTM_CAMPAIGN' | 'UTM_CAMPAIGN_BROAD';
    campanha: string;
    canal: 'flow' | 'broadcast';
    paginas: CampanhaDuplicadaPagina[];
    qtd_paginas: number;
    qtd_ativas: number;
    idiomas_diferentes: boolean;
    /** erro = flow repetido (deveria ser 1:1); alerta = broadcast entre idiomas. */
    severidade: 'erro' | 'alerta' | 'ok';
}

export interface CampanhasDuplicadas {
    total_grupos: number;
    grupos_flow: number;
    grupos_broadcast: number;
    paginas_afetadas: number;
    grupos: CampanhaDuplicadaGrupo[];
}

export const getCampanhasDuplicadas = async (params?: {
    /** Id do banco (Conexões tem este). */
    page_id?: number;
    /** Id da Meta (a Tela de Leads tem este). */
    meta_page_id?: string;
    apenas_ativas?: boolean;
    incluir_esperados?: boolean;
}): Promise<CampanhasDuplicadas> => {
    const response = await apiClient.get('/meta/pages/variables/campanhas-duplicadas', { params });
    return response.data;
};

export const createPageCategory = async (data: { name: string }): Promise<PageCategory> => {
    const response = await apiClient.post('/meta/page-categories', data);
    return response.data;
};

export const updatePageCategory = async (id: number, data: { name: string }): Promise<PageCategory> => {
    const response = await apiClient.patch(`/meta/page-categories/${id}`, data);
    return response.data;
};

export const deletePageCategory = async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/meta/page-categories/${id}`);
    return response.data;
};

// ====================================
// BROADCAST V2 SETTINGS
// ====================================

export const getBroadcastV2Settings = async (): Promise<BroadcastV2Settings> => {
    const response = await apiClient.get('/meta/broadcast-v2-settings');
    return response.data;
};

export const updateBroadcastV2Settings = async (active_leads_only: boolean): Promise<BroadcastV2Settings> => {
    const response = await apiClient.put('/meta/broadcast-v2-settings', { active_leads_only });
    return response.data;
};

export const listAppRateLimits = async (): Promise<AppRateLimitListResponse> => {
    const response = await apiClient.get('/meta/broadcast-v2-settings/rate-limits');
    return response.data;
};

export const upsertAppRateLimit = async (app_id: number, max_per_sec: number): Promise<AppRateLimit> => {
    const response = await apiClient.put('/meta/broadcast-v2-settings/rate-limits', { app_id, max_per_sec });
    return response.data;
};

export const bulkUpsertAppRateLimits = async (app_ids: number[], max_per_sec: number): Promise<AppRateLimit[]> => {
    const response = await apiClient.put('/meta/broadcast-v2-settings/rate-limits/bulk', { app_ids, max_per_sec });
    return response.data;
};

export const deleteAppRateLimit = async (app_id: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/meta/broadcast-v2-settings/rate-limits/${app_id}`);
    return response.data;
};

// ====================================
// ORCHESTRATOR SETTINGS
// Configs operacionais do orquestrador de broadcast — antes só existiam
// como env var no docker-compose (exigiam SSH + recriar container).
// ====================================

export const listOrchestratorSettings = async (): Promise<OrchestratorSettingListResponse> => {
    const response = await apiClient.get('/meta/orchestrator-settings');
    return response.data;
};

export const updateOrchestratorSetting = async (
    key_name: string,
    value: string,
): Promise<OrchestratorSettingUpdateResponse> => {
    const response = await apiClient.put(`/meta/orchestrator-settings/${key_name}`, { value });
    return response.data;
};

export const getOrchestratorSettingHistory = async (
    key_name: string,
): Promise<OrchestratorSettingHistoryResponse> => {
    const response = await apiClient.get(`/meta/orchestrator-settings/${key_name}/history`);
    return response.data;
};

export const getAlertContacts = async (): Promise<AlertContact[]> => {
    const response = await apiClient.get('/meta/alert-contacts');
    return response.data;
};

export const createAlertContact = async (data: AlertContactInput): Promise<AlertContact> => {
    const response = await apiClient.post('/meta/alert-contacts', data);
    return response.data;
};

export const updateAlertContact = async (id: number, data: AlertContactInput): Promise<AlertContact> => {
    const response = await apiClient.put(`/meta/alert-contacts/${id}`, data);
    return response.data;
};

export const deleteAlertContact = async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/meta/alert-contacts/${id}`);
    return response.data;
};
